// -----------------------------------------------------------------------------
// ExpandToggle
// - Reusable rotating arrow icon for expandable sections.
// - Keeps expand/collapse UI consistent across the app.
// - "default" variant: translucent info-blue pill (light backgrounds)
// - "onDark" variant: frosted white pill (royal-blue / dark backgrounds)
// -----------------------------------------------------------------------------

import { ExpandMore } from "@mui/icons-material";
import { IconButton, alpha, useTheme } from "@mui/material";

interface ExpandToggleProps {
  expanded: boolean;
  /** Visual variant — "onDark" for dark/royal-blue headers. */
  variant?: "default" | "onDark";
  /** @deprecated Use `variant` instead. Override icon color. */
  color?: string;
}

const ExpandToggle = ({ expanded, variant = "default", color }: ExpandToggleProps) => {
  const theme = useTheme();

  const isDark = variant === "onDark" || !!color;

  const bgColor = isDark
    ? alpha(theme.palette.common.white, 0.12)
    : alpha(theme.palette.info.main, 0.1);

  const fgColor = isDark
    ? (color ?? alpha(theme.palette.common.white, 0.85))
    : theme.palette.info.main;

  return (
    <IconButton
      size="small"
      disableRipple
      sx={{
        width: 24,
        height: 24,
        p: 0,
        backgroundColor: bgColor,
        color: fgColor,
        border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.2) : "transparent"}`,
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        transition: "all 0.25s ease",
        "&:hover": {
          backgroundColor: isDark
            ? alpha(theme.palette.common.white, 0.22)
            : alpha(theme.palette.primary.main, 0.18),
          color: isDark
            ? theme.palette.common.white
            : theme.palette.primary.main,
          borderColor: isDark
            ? alpha(theme.palette.common.white, 0.4)
            : alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <ExpandMore sx={{ fontSize: 15 }} />
    </IconButton>
  );
};

export default ExpandToggle;
